import { useMemo } from 'react';

import { useDynamicEntityCMSDataContext, useDynamicEntityStoreContext } from '../../../contexts';

import TextInput from '../../../../inputs/form-inputs/TextInput';
import ImageSelectorModal from './ImageSelectorModal';
import ImageUploadModal from './ImageUploadModal';
import ImageCropModal from './ImageCropModal';

const EmptyImageInput = ({ id, onChange }) => {
  return (
    <div className="image-input">
      <div className="image-preview">
        <div className="background">
          <i className="fe fe-image" />
          <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target={`#imageSelector-${id}-Modal`}>
            Select Image
          </button>
        </div>
      </div>
      <ImageSelectorModal {...{ id, onChange }} />
      <ImageUploadModal {...{ id, onChange }} />
    </div>
  );
};

const ExistingImageInput = ({ id, value, onChange, showTitle }) => {
  const { images, isLoadingImages } = useDynamicEntityCMSDataContext();

  const image = useMemo(() => images?.find((image) => image.id === value.id), [images, value.id]);

  const handleAltOnChange = (e) => {
    onChange({ ...value, alt: e.target.value });
  };

  const handleTitleOnChange = (e) => {
    onChange({ ...value, title: e.target.value });
  };

  const handleRemoveOnChange = () => {
    onChange(null);
  };

  const isLoading = isLoadingImages && !image;
  const isUnavailable = !isLoadingImages && !image && !value.url;
  const hasImage = !!image;

  const altDisabled = isLoading || isUnavailable;
  const titleDisabled = isLoading || isUnavailable;
  const showCrop = hasImage;
  const imgSrc = hasImage ? image.url : value.url;

  return (
    <div className="image-input">
      <div className="image-preview">
        {isLoading ? (
          <div className="image-not-found">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="image-buttons">
              {showCrop && (
                <button className="image-button" data-bs-toggle="modal" data-bs-target={`#imageCrop-${id}-Modal`}>
                  <i className="fe fe-crop" />
                </button>
              )}
              <button className="image-button" data-bs-toggle="modal" data-bs-target={`#imageSelector-${id}-Modal`}>
                <i className="fe fe-upload" />
              </button>
              <button className="image-button" onClick={handleRemoveOnChange}>
                <i className="fe fe-x" />
              </button>
            </div>
            {isUnavailable ? (
              <div className="image-not-found">
                <span>Preview unavailable.</span>
              </div>
            ) : (
              <div className="media-preview">
                <img className="image" src={imgSrc} />
              </div>
            )}
          </>
        )}
      </div>
      <div className="d-flex align-items-center justify-content-center gap-3 w-100">
        <div className="d-flex flex-column gap-2 w-100">
          <label className="text-muted">Image Alt</label>
          <TextInput value={value.alt ?? ''} onChange={handleAltOnChange} disabled={altDisabled} />
        </div>
        {showTitle && (
          <div className="d-flex flex-column gap-2 w-100">
            <label className="text-muted">Image Title</label>
            <TextInput value={value.title ?? ''} onChange={handleTitleOnChange} disabled={titleDisabled} />
          </div>
        )}
      </div>
      <ImageSelectorModal {...{ id, onChange }} />
      {showCrop && <ImageCropModal {...{ id, image, onChange }} />}
      <ImageUploadModal {...{ id, onChange }} />
    </div>
  );
};

export default function ImageInput({ id, value, onChange, showTitle = true }) {
  // Convert ID into a single string if it's an array
  if (Array.isArray(id)) {
    id = id.join('-');
  }

  if (value && (value.id || value.url)) {
    return <ExistingImageInput {...{ id, value, onChange, showTitle }} />;
  }

  return <EmptyImageInput {...{ id, value, onChange }} />;
}
